import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { Pagination } from '@api/abstracts/models';
import { AbstractDynamicComponent } from '@components/abstracts';
import {
  DEFAULT_ITEMS_PER_PAGE,
  DEFAULT_PAGE_NUMBER,
  OFFSET_FROM_END,
} from '@components/listing-modeles-page-filtre/config';
import { ListingModelesPageFiltre } from '@components/listing-modeles-page-filtre/models';
import { FilterSelectComponent } from '@shared/components/filter-select';
import { KitchenModelCardComponent } from '@shared/components/kitchen-model-card';
import { ViewportObserverDirective } from '@shared/directives';
import { Kitchen, Option } from '@shared/models';
import { ElasticSearchService } from '@shared/services/elastic-search';
import { PayloadModels } from '@shared/services/elastic-search/models';
import { LanguageService } from '@shared/services/language';
import { LogsService } from '@shared/services/logs';
import { UrlParamsService } from '@shared/services/url-params';
import { take, tap } from 'rxjs';

@Component({
  selector: 'app-listing-modeles-page-filtre',
  templateUrl: './listing-modeles-page-filtre.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [KitchenModelCardComponent, FilterSelectComponent, ViewportObserverDirective],
})
export class ListingModelesPageFiltreComponent extends AbstractDynamicComponent<ListingModelesPageFiltre> {
  protected readonly kitchens = signal<Kitchen[]>([]);
  protected readonly preselectedSwatches = signal<Record<string, string>>({});

  private readonly elasticSearchService = inject(ElasticSearchService);
  private readonly urlParamsService = inject(UrlParamsService);
  private readonly languageService = inject(LanguageService);
  readonly filterSelectionOptions: Option[] = [
    {
      key: 'our_selection',
      label: this.languageService.instant('COMPONENT.TITRE-FILTRES-MODELES.OUR_SELECTION'),
      isSelected: true,
    },
    {
      key: 'price_ascending',
      label: this.languageService.instant('COMPONENT.TITRE-FILTRES-MODELES.PRICE_ASCENDING'),
    },
    {
      key: 'price_descending',
      label: this.languageService.instant('COMPONENT.TITRE-FILTRES-MODELES.PRICE_DESCENDING'),
    },
  ];
  private readonly logs = inject(LogsService);
  private readonly params = this.urlParamsService.params;
  private readonly currentPageFromUrl = computed(() =>
    Number(this.urlParamsService.param('listing_page')() ?? DEFAULT_PAGE_NUMBER)
  );
  private firstInit = true;
  private readonly offsetFromEnd = OFFSET_FROM_END > DEFAULT_ITEMS_PER_PAGE ? DEFAULT_ITEMS_PER_PAGE : OFFSET_FROM_END;
  private totalItems = 0;
  private remainingItems = 0;
  private nbKitchensLoaded = 0;
  private isAllKitchensLoaded = false;
  private filtersPrev = '';

  constructor() {
    super();
    effect(() => {
      const params = this.params();
      if (params['reset']) {
        untracked(() => this.urlParamsService.removeParams('reset'));
        return;
      }
      const currentPage = this.currentPageFromUrl() ?? 1;
      const itemFrom = this.nbKitchensLoaded;
      const itemsToFetch = this.firstInit ? currentPage * DEFAULT_ITEMS_PER_PAGE : DEFAULT_ITEMS_PER_PAGE;
      const { filter_selection } = this.params() as Record<string, string>;
      if (!filter_selection) return;
      const isOurSelection = filter_selection === 'our_selection';
      if (filter_selection || currentPage) {
        const sort = !isOurSelection ? filter_selection : undefined;
        const filters = `${sort}`;
        if (!this.firstInit && this.filtersPrev && filters !== this.filtersPrev) {
          this.resetListing();
          return;
        }
        this.filtersPrev = filters;
        if (!this.firstInit && this.totalItems !== 0 && this.remainingItems <= 0) return;
        this.loadKitchens({
          enableCustomBrandSelection: isOurSelection,
          sort,
          itemFrom,
          itemsToFetch,
        });
      }
    });
  }

  onReviewViewportEnter($event: boolean, kitchenId: string) {
    if ($event) {
      const fifthFromEnd = this.kitchens().at(-this.offsetFromEnd);
      if (this.nbKitchensLoaded < this.totalItems && !this.isAllKitchensLoaded) {
        if (fifthFromEnd?.id === kitchenId) {
          this.loadMore();
        }
      }
    }
  }

  loadMore() {
    this.urlParamsService.mergeParams({ listing_page: this.currentPageFromUrl() + 1 });
  }

  private loadKitchens(query: PayloadModels) {
    this.elasticSearchService
      .getKitchensByMultipleFilters({
        ...(this.data.front && this.data.front.length >= 1 ? { front: this.data.front } : {}),
        ...(this.data.layout && this.data.layout.length >= 1 ? { layout: this.data.layout } : {}),
        ...(this.data.space && this.data.space.length >= 1 ? { space: this.data.space } : {}),
        ...(this.data.style && this.data.style.length >= 1 ? { style: this.data.style } : {}),
        sortByWeight: !!query.enableCustomBrandSelection,
        ...(query.sort ? { sort: query.sort } : {}),
        from: query.itemFrom,
        size: query.itemsToFetch,
      })
      .pipe(
        take(1),
        tap((list: Pagination<Kitchen>) => {
          if (this.totalItems === 0) {
            this.totalItems = list.totalItems;
          }
          if (query.itemFrom === 0) {
            untracked(() => this.kitchens.set(list.items));
          } else {
            untracked(() => this.kitchens.update((kitchens) => [...kitchens, ...list.items]));
          }
          this.firstInit = false;
          this.nbKitchensLoaded = untracked(() => this.kitchens().length);
          this.remainingItems = this.totalItems - this.nbKitchensLoaded;
          if (this.remainingItems <= 0) {
            this.isAllKitchensLoaded = true;
            this.logs.info(
              'ListingModelesPageFiltreComponent',
              'All kitchen models have been loaded',
              this.nbKitchensLoaded
            );
          }
          untracked(() => {
            const newSwatches = list.items.reduce(
              (acc, cur) => {
                acc[cur.id] = (
                  cur.variants.find((variant) =>
                    variant.fronts?.some((front) => this.data.front?.includes(front.label))
                  ) ?? cur.variants[0]!
                ).id;
                return acc;
              },
              {} as Record<string, string>
            );

            this.preselectedSwatches.update((existing) => ({
              ...existing,
              ...newSwatches,
            }));
          });
        })
      )
      .subscribe({
        error: (err) =>
          this.logs.error('ListingModelesPageFiltreComponent', 'getKitchensByMultipleFilters failed', err),
      });
  }

  private resetListing() {
    this.firstInit = true;
    this.totalItems = 0;
    this.remainingItems = 0;
    this.nbKitchensLoaded = 0;
    this.isAllKitchensLoaded = false;
    this.urlParamsService.mergeParams({ listing_page: 1, reset: true });
  }
}
